import * as React from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, Link, MenuItem, Radio, RadioGroup,
    Stack, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import SyncIcon from '@mui/icons-material/Sync';
import {SettingsCard} from '../layouts/SettingsCard';
import {useCalendarFeeds, useConnectCalendarFeed, useDisconnectCalendarFeed, useSyncCalendarFeed} from '../../data/calendar';
import {apiErrorMessage, isNetworkError} from '../../utilities/apiError';
import {CalendarProvider, calendarAddressProvider, countsMessage, feedStatusLine, providerLabel} from '../../utilities/calendarFeeds';
import type {CalendarFeed} from '../../contract';

type Section = { id: string, title: string };

const failure = (error: unknown) => (isNetworkError(error) ? 'Network error. Please try again later.' : apiErrorMessage(error, 'Something went wrong. Please try again.'));

// Connecting a Google or Apple calendar to the board: its events become tasks, kept up to date. Owners and editors only (the server checks too).
export function CalendarFeedsCard({boardId, sections}: { boardId: string, sections: Section[] }) {
    const {data, isLoading, isError} = useCalendarFeeds(boardId);
    const [connecting, setConnecting] = React.useState(false);
    const [disconnecting, setDisconnecting] = React.useState<CalendarFeed | null>(null);
    const [message, setMessage] = React.useState<{ severity: 'success' | 'error', text: string } | null>(null);
    const {mutate: syncFeed, isPending: isSyncing, variables: syncing} = useSyncCalendarFeed(boardId);

    const sectionTitle = (id: string) => sections.find((section) => section.id === id)?.title ?? 'a section that was deleted';

    function readNow(feed: CalendarFeed) {
        setMessage(null);
        syncFeed(feed.id, {
            onSuccess: (result) => setMessage(result.counts ? {severity: 'success', text: `${feed.name}: ${countsMessage(result.counts)}`} : {severity: 'error', text: `${feed.name} could not be read: see below.`}),
            onError: (error) => setMessage({severity: 'error', text: failure(error)}),
        });
    }

    return (
        <SettingsCard
            title="Calendars"
            description="Show the events of a Google or Apple calendar as tasks on this board. They are kept up to date: a new event adds a task, a changed event updates it, and a cancelled event takes it away."
            action={data?.available ? <Button variant="outlined" startIcon={<EventIcon/>} onClick={() => setConnecting(true)}>Connect a calendar</Button> : undefined}
        >
            {isLoading && <Typography color="text.secondary">Loading…</Typography>}
            {isError && <Alert severity="error">The calendars could not be loaded.</Alert>}
            {data && !data.available && <Alert severity="info">Importing a Google Calendar is not set up on this server.</Alert>}
            {message && <Alert severity={message.severity} onClose={() => setMessage(null)} sx={{mb: 2}}>{message.text}</Alert>}

            {data?.available && data.feeds.length === 0 && (
                <Typography variant="body2" color="text.secondary">No calendar is connected. Events from the next {data.daysAhead} days are imported.</Typography>
            )}

            <Stack spacing={1.5}>
                {data?.feeds.map((feed) => (
                    <Box key={feed.id} sx={{border: 1, borderColor: feed.status === 'error' ? 'error.light' : 'divider', borderRadius: 1.5, p: 1.5}}>
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                            <EventIcon fontSize="small" color={feed.status === 'error' ? 'error' : 'action'}/>
                            <Typography sx={{fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere'}}>{feed.name}</Typography>
                            <Chip size="small" variant="outlined" label={`${providerLabel(feed.provider ?? 'google')} → ${sectionTitle(feed.sectionId)}`}/>
                            <Box sx={{flexGrow: 1}}/>
                            <Button size="small" startIcon={<SyncIcon/>} disabled={isSyncing} onClick={() => readNow(feed)}>
                                {isSyncing && syncing === feed.id ? 'Reading…' : 'Read now'}
                            </Button>
                            <Button size="small" color="error" onClick={() => setDisconnecting(feed)}>Disconnect</Button>
                        </Box>
                        <Typography variant="body2" color={feed.status === 'error' ? 'error' : 'text.secondary'} sx={{mt: 0.5}}>
                            {feedStatusLine(feed, Date.now())}
                        </Typography>
                        {feed.notice && <Typography variant="body2" color="text.secondary">{feed.notice}</Typography>}
                    </Box>
                ))}
            </Stack>

            {connecting && <ConnectDialog boardId={boardId} sections={sections} onClose={() => setConnecting(false)} onDone={(text) => { setConnecting(false); setMessage({severity: 'success', text}); }}/>}
            {disconnecting && <DisconnectDialog boardId={boardId} feed={disconnecting} onClose={() => setDisconnecting(null)} onDone={(text) => { setDisconnecting(null); setMessage({severity: 'success', text}); }}/>}
        </SettingsCard>
    );
}

function ConnectDialog({boardId, sections, onClose, onDone}: { boardId: string, sections: Section[], onClose: () => void, onDone: (message: string) => void }) {
    const [url, setUrl] = React.useState('');
    const [provider, setProvider] = React.useState<CalendarProvider>('google');
    const [sectionId, setSectionId] = React.useState(sections[0]?.id ?? '');
    const [touched, setTouched] = React.useState(false);
    const {mutate: connect, isPending, error} = useConnectCalendarFeed(boardId);

    // What was pasted decides whose instructions are shown: someone who pastes an Apple link is not left reading Google's steps.
    const recognised = calendarAddressProvider(url);
    React.useEffect(() => { if (recognised) setProvider(recognised); }, [recognised]);

    const looksRight = recognised !== null;
    const canConnect = looksRight && !!sectionId && !isPending;

    return (
        <Dialog open onClose={isPending ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="connect-calendar-title">
            <DialogTitle id="connect-calendar-title">Connect a calendar</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{pt: 0.5}}>
                    <ToggleButtonGroup exclusive size="small" value={provider} onChange={(_, value: CalendarProvider | null) => { if (value) setProvider(value); }} aria-label="Which calendar">
                        <ToggleButton value="google">Google Calendar</ToggleButton>
                        <ToggleButton value="apple">Apple Calendar</ToggleButton>
                    </ToggleButtonGroup>

                    {provider === 'google' ? (
                        <>
                            <Typography variant="body2" component="div">
                                Google gives each calendar a private address that this board can read:
                                <ol style={{margin: '8px 0 0', paddingLeft: 20}}>
                                    <li>Open <Link href="https://calendar.google.com/calendar/u/0/r/settings" target="_blank" rel="noopener noreferrer">Google Calendar settings</Link> on a computer.</li>
                                    <li>Under &quot;Settings for my calendars&quot;, choose the calendar.</li>
                                    <li>Scroll to &quot;Integrate calendar&quot; and copy the <strong>Secret address in iCal format</strong>.</li>
                                    <li>Paste it below.</li>
                                </ol>
                            </Typography>
                            <Alert severity="warning" sx={{py: 0}}>
                                Anyone who has that address can read the whole calendar. It is kept encrypted here and never shown again. If it ever leaks, use &quot;Reset secret address&quot; in Google Calendar.
                            </Alert>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" component="div">
                                Apple calendars are shared with a public link that this board can read:
                                <ol style={{margin: '8px 0 0', paddingLeft: 20}}>
                                    <li>Find the calendar in the Calendar app on a Mac (right-click it, then <strong>Share Calendar…</strong>), on an iPhone (tap the <strong>ⓘ</strong> next to it), or on iCloud.com (the share icon next to it).</li>
                                    <li>Turn on <strong>Public Calendar</strong>, then <strong>Copy Link</strong>.</li>
                                    <li>Paste it below. It starts with <code>webcal://</code>.</li>
                                </ol>
                            </Typography>
                            <Alert severity="warning" sx={{py: 0}}>
                                Apple only offers public links: anyone who gets this link can read the calendar. It is not listed anywhere, but it is not private either. Here it is kept
                                encrypted and never shown again. To stop sharing it, turn <strong>Public Calendar</strong> off in the Calendar app.
                            </Alert>
                        </>
                    )}

                    <TextField
                        autoFocus fullWidth label={provider === 'google' ? 'Secret address in iCal format' : 'Public calendar link'} value={url} autoComplete="off" spellCheck={false}
                        onChange={(e) => setUrl(e.target.value)} onBlur={() => setTouched(true)} disabled={isPending}
                        error={touched && !!url && !looksRight}
                        helperText={touched && url && !looksRight ? 'That is not a Google or Apple calendar address. Google\'s starts with https://calendar.google.com/calendar/ical/ and Apple\'s with webcal://' : ' '}
                    />
                    <TextField select fullWidth label="Put the events in section" value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={isPending || sections.length === 0}
                        helperText={sections.length === 0 ? 'Add a section to the board first.' : 'You can move a task to another section afterwards.'}>
                        {sections.map((section) => <MenuItem key={section.id} value={section.id}>{section.title}</MenuItem>)}
                    </TextField>
                    {error && <Alert severity="error">{failure(error)}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button
                    variant="contained" disabled={!canConnect}
                    onClick={() => connect({url: url.trim(), sectionId, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone}, {onSuccess: (result) => onDone(`Connected ${result.feed.name}: ${countsMessage(result.counts)}`)})}
                >
                    {isPending ? 'Connecting…' : 'Connect'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function DisconnectDialog({boardId, feed, onClose, onDone}: { boardId: string, feed: CalendarFeed, onClose: () => void, onDone: (message: string) => void }) {
    const [deleteTasks, setDeleteTasks] = React.useState(false);
    const {mutate: disconnect, isPending, error} = useDisconnectCalendarFeed(boardId);

    return (
        <Dialog open onClose={isPending ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="disconnect-calendar-title">
            <DialogTitle id="disconnect-calendar-title">Disconnect &quot;{feed.name}&quot;?</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{mb: 1}}>The board stops following this calendar. What should happen to the tasks it made?</DialogContentText>
                <RadioGroup value={deleteTasks ? 'delete' : 'keep'} onChange={(e) => setDeleteTasks(e.target.value === 'delete')}>
                    <FormControlLabel value="keep" control={<Radio/>} label="Keep them as ordinary tasks"/>
                    <FormControlLabel value="delete" control={<Radio/>} label="Delete them"/>
                </RadioGroup>
                {!deleteTasks && <Typography variant="caption" color="text.secondary">Kept tasks no longer follow the calendar. Connecting it again later would add its events a second time.</Typography>}
                {error && <Alert severity="error" sx={{mt: 1}}>{failure(error)}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isPending}>Cancel</Button>
                <Button color="error" variant="contained" disabled={isPending}
                    onClick={() => disconnect({feedId: feed.id, deleteTasks}, {onSuccess: (result) => onDone(deleteTasks ? `Disconnected ${feed.name} and deleted ${result.removedTasks} task${result.removedTasks === 1 ? '' : 's'}.` : `Disconnected ${feed.name}. Its tasks were kept.`)})}>
                    Disconnect
                </Button>
            </DialogActions>
        </Dialog>
    );
}
